import React, { useEffect, useMemo, useState } from "react";
import {
  View,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
  Alert,
} from "react-native";
import Text from "@components/CustomText";
import ChevronRight from "@assets/svg/ChevronRight";
import BottomSheet from "@components/ds/BottomSheet";
import SheetButtons from "@components/ds/SheetButtons";
import StatusBadge from "./StatusBadge";
// Locality-based grouping/scoping lives in a sibling module so it can be
// unit-tested without React Native. A shared crate appears under EACH
// locality it serves, scoped to that locality's own customers.
import { groupForDb } from "./addBundleGrouping";
import { tokens } from "../styles";

// Add Bundle — bidirectional manage sheet.
//
// Shape:
//   ┌── Locality 1 (header — only routes subscribed to this DB show)
//   │     ┌── Crate A · 5 customers · [▶ expand]   [☑] ← all on this DB
//   │     │     ┌── Cx 1 (on this DB) [☑]
//   │     │     ├── Cx 2 (on another DB) [☐]
//   │     │     └── …
//   │     └── Crate B · 2 customers · [▶ expand]   [─] ← partial
//   └── Locality 2 …
//
// What changed (2026-05-15): used to be an "add-only" sheet that hid
// fully-assigned-to-this-DB crates entirely. Now the sheet renders every
// crate in the DB's subscribed localities and pre-checks customers that
// already belong to this DB. Saving diffs against the initial state:
// newly-checked → assign; newly-unchecked → unassign. Matches the web
// app: any change you can make on web you can make here, and vice versa.
// A confirmation modal pops up before save if the diff contains any
// unassigns -- guards against an accidental tap unassigning a customer
// the executive didn't mean to touch.
//
// Grouping/scoping is by the CUSTOMER's own locality (tp.cx_locality),
// not the crate's tag. A physical crate can bundle several neighbouring
// localities; it is listed under EACH locality it serves and shows only
// that locality's customers (see groupForDb in ./addBundleGrouping). This
// mirrors web, where assigning a locality gives only that locality's own-
// address customers. tp.cx_locality and db.routes[*].route_name both come
// from data.data.DC[*].dc, so they share one source string and compare
// equal. Route IDs (bigints in cp_db_routes_v2) are used only for the
// assignDbs payload's route_id — a customer is assigned with their own
// locality group's route_id, never a foreign one.

const AddBundleSheet = ({
  visible,
  db,
  crates = [],
  cpId,
  onClose,
  onSubmit,
  submitting,
}) => {
  const [selectedCx, setSelectedCx] = useState(new Set());
  const [openCrates, setOpenCrates] = useState(new Set());

  const groups = useMemo(() => groupForDb(crates, db), [crates, db]);

  // Customers currently assigned to THIS DB across the visible (locality-
  // scoped) crates. Drives the pre-check state and the Save diff.
  const initiallyAssignedCx = useMemo(() => {
    const dbId = String(db?.cp_db_id || "");
    const set = new Set();
    if (!dbId) return set;
    groups.forEach((g) => {
      g.crates.forEach(({ cxs }) => {
        cxs.forEach((tp) => {
          if (!tp?.user_id) return;
          if (String(tp?.cp_db_id || "") !== dbId) return;
          if (tp?.status && tp.status !== "ASSIGNED") return;
          set.add(String(tp.user_id));
        });
      });
    });
    return set;
  }, [groups, db]);

  useEffect(() => {
    if (visible) {
      // Reset selection to the current truth from the snapshot — every
      // customer already on this DB starts checked. The user toggles
      // from there.
      setSelectedCx(new Set(initiallyAssignedCx));
      setOpenCrates(new Set());
    }
  }, [visible, initiallyAssignedCx]);

  const toggleCx = (cxId) => {
    const key = String(cxId);
    setSelectedCx((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleCrate = (cxs) => {
    const list = Array.isArray(cxs) ? cxs : [];
    const allSelected =
      list.length > 0 &&
      list.every((tp) => selectedCx.has(String(tp?.user_id)));
    setSelectedCx((prev) => {
      const next = new Set(prev);
      list.forEach((tp) => {
        if (!tp?.user_id) return;
        if (allSelected) next.delete(String(tp.user_id));
        else next.add(String(tp.user_id));
      });
      return next;
    });
  };

  // Group-level select-all toggle. Walks every customer across every
  // crate in the locality group so the user can pick the whole locality
  // in one tap.
  const collectGroupCxIds = (g) => {
    const ids = [];
    g.crates.forEach(({ cxs }) => {
      cxs.forEach((tp) => {
        if (tp?.user_id) ids.push(String(tp.user_id));
      });
    });
    return ids;
  };

  const toggleGroup = (g) => {
    const ids = collectGroupCxIds(g);
    if (ids.length === 0) return;
    const allSelected = ids.every((id) => selectedCx.has(id));
    setSelectedCx((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => {
        if (allSelected) next.delete(id);
        else next.add(id);
      });
      return next;
    });
  };

  const groupState = (g) => {
    const ids = collectGroupCxIds(g);
    if (ids.length === 0) return "empty";
    let selectedCount = 0;
    ids.forEach((id) => {
      if (selectedCx.has(id)) selectedCount += 1;
    });
    if (selectedCount === 0) return "none";
    if (selectedCount === ids.length) return "all";
    return "some";
  };

  const toggleOpen = (crateId) => {
    const key = String(crateId);
    setOpenCrates((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const crateState = (cxs) => {
    const list = Array.isArray(cxs) ? cxs : [];
    if (list.length === 0) return "empty";
    let selectedCount = 0;
    list.forEach((tp) => {
      if (selectedCx.has(String(tp?.user_id))) selectedCount += 1;
    });
    if (selectedCount === 0) return "none";
    if (selectedCount === list.length) return "all";
    return "some";
  };

  // Diff used by the footer label, the confirm modal, and the submit
  // payload builder. toAssign = newly-checked cxs; toUnassign = newly-
  // unchecked cxs that were initially on this DB.
  const diff = useMemo(() => {
    const toAssign = new Set();
    const toUnassign = new Set();
    selectedCx.forEach((id) => {
      if (!initiallyAssignedCx.has(id)) toAssign.add(id);
    });
    initiallyAssignedCx.forEach((id) => {
      if (!selectedCx.has(id)) toUnassign.add(id);
    });
    return { toAssign, toUnassign };
  }, [selectedCx, initiallyAssignedCx]);

  const totalChange = diff.toAssign.size + diff.toUnassign.size;

  const buildTps = () => {
    // Each customer's cx_locality places them in exactly ONE locality
    // group, so they're assigned with that group's route_id — their own
    // locality's route, never a foreign one. Dedup by cx because the
    // backend's cp_db_assigned_touchpoints is one-row-per-cx and a cx can
    // appear across several crates within the same locality.
    const seen = new Set();
    const tps = [];
    groups.forEach((g) => {
      const routeId = g?.route?.route_id;
      g.crates.forEach(({ cxs }) => {
        cxs.forEach((tp) => {
          if (!tp?.user_id) return;
          const id = String(tp.user_id);
          if (seen.has(id)) return;
          if (diff.toAssign.has(id)) {
            if (routeId == null) return;
            seen.add(id);
            tps.push({
              cp_id: cpId,
              cx_id: tp.user_id,
              cp_db_id: db?.cp_db_id,
              route_id: routeId,
            });
          } else if (diff.toUnassign.has(id)) {
            seen.add(id);
            tps.push({
              cp_id: cpId,
              cx_id: tp.user_id,
              cp_db_id: db?.cp_db_id,
              route_id: "UNASSIGN",
            });
          }
        });
      });
    });
    return tps;
  };

  const submitNow = () => {
    const tps = buildTps();
    if (tps.length === 0) {
      onClose();
      return;
    }
    const successParts = [];
    if (diff.toAssign.size > 0) {
      successParts.push(`assigned ${diff.toAssign.size}`);
    }
    if (diff.toUnassign.size > 0) {
      successParts.push(`unassigned ${diff.toUnassign.size}`);
    }
    onSubmit({
      tps,
      cp_db_id: db?.cp_db_id,
      route_id:
        tps.find((t) => t.route_id !== "UNASSIGN")?.route_id ??
        tps[0]?.route_id,
      successMessage: `${db?.db_name || "DB"} · ${successParts.join(" · ")}`,
    });
  };

  const handleConfirm = () => {
    if (!db || totalChange === 0) return;
    if (diff.toUnassign.size > 0) {
      const n = diff.toUnassign.size;
      Alert.alert(
        "Confirm unassign",
        `This will unassign ${n} customer${n === 1 ? "" : "s"} from ${
          db?.db_name || "this DB"
        }. Continue?`,
        [
          { text: "Cancel", style: "cancel" },
          {
            text: "Confirm",
            style: "destructive",
            onPress: submitNow,
          },
        ],
        { cancelable: true },
      );
    } else {
      submitNow();
    }
  };

  const noRoutes = !Array.isArray(db?.routes) || db.routes.length === 0;
  const hasAnyVisibleCrates = groups.some((g) => g.crates.length > 0);

  const subtitleText = noRoutes
    ? "Select localities for this DB first"
    : !hasAnyVisibleCrates
      ? "No crates today for the subscribed localities"
      : totalChange === 0
        ? "Tap a customer or crate to add or remove"
        : `${diff.toAssign.size} to assign · ${diff.toUnassign.size} to unassign`;

  const confirmLabel =
    totalChange === 0
      ? "No changes"
      : diff.toUnassign.size === 0
        ? `Assign ${diff.toAssign.size} customer${
            diff.toAssign.size === 1 ? "" : "s"
          }`
        : diff.toAssign.size === 0
          ? `Unassign ${diff.toUnassign.size} customer${
              diff.toUnassign.size === 1 ? "" : "s"
            }`
          : `Save (+${diff.toAssign.size} / −${diff.toUnassign.size})`;

  return (
    <BottomSheet
      visible={visible}
      title={`Add Crates · ${db?.db_name || "DB"}`}
      subtitle={subtitleText}
      onClose={onClose}
      footer={
        <SheetButtons
          onCancel={onClose}
          onConfirm={handleConfirm}
          loading={submitting}
          disabled={totalChange === 0}
          confirmLabel={confirmLabel}
        />
      }
    >
      {noRoutes ? (
        <Text variant="caption_2" style={styles.empty}>
          Tap “Select Locality” on the DB card to choose which localities this
          DB will deliver, then come back to add bundles.
        </Text>
      ) : !hasAnyVisibleCrates ? (
        <Text variant="caption_2" style={styles.empty}>
          No crates are available today for the localities this DB is subscribed
          to.
        </Text>
      ) : (
        <ScrollView style={styles.scroll}>
          {groups.map((g) => {
            const cratesForGroup = g.crates;
            // Locality subscribed but no crates today — keep the header so
            // the user sees that the subscription is in place.
            if (cratesForGroup.length === 0) {
              return (
                <View key={String(g.route?.route_id)} style={styles.group}>
                  <View style={styles.groupHeader}>
                    <View style={styles.groupHeaderText}>
                      <Text variant="caption_2" style={styles.groupLabel}>
                        Locality
                      </Text>
                      <Text variant="title_16" style={styles.groupTitle}>
                        {g.route?.route_name ||
                          `Locality ${g.route?.route_id || ""}`}
                      </Text>
                      <Text variant="caption_2" style={styles.groupMeta}>
                        No crates today
                      </Text>
                    </View>
                  </View>
                </View>
              );
            }
            const groupCxCount = cratesForGroup.reduce(
              (acc, entry) => acc + (entry?.cxs?.length || 0),
              0,
            );
            const gState = groupState(g);
            return (
              <View key={String(g.route?.route_id)} style={styles.group}>
                <TouchableOpacity
                  activeOpacity={0.85}
                  onPress={() => toggleGroup(g)}
                  style={styles.groupHeader}
                >
                  <View
                    style={[
                      styles.checkbox,
                      gState === "all" && styles.checkboxSelected,
                      gState === "some" && styles.checkboxPartial,
                    ]}
                  >
                    {gState === "all" ? (
                      <Text variant="caption_2" style={styles.tick}>
                        ✓
                      </Text>
                    ) : gState === "some" ? (
                      <Text variant="caption_2" style={styles.tick}>
                        ─
                      </Text>
                    ) : null}
                  </View>
                  <View style={styles.groupHeaderText}>
                    <Text variant="caption_2" style={styles.groupLabel}>
                      Locality
                    </Text>
                    <Text variant="title_16" style={styles.groupTitle}>
                      {g.route?.route_name ||
                        `Locality ${g.route?.route_id || ""}`}
                    </Text>
                    <Text variant="caption_2" style={styles.groupMeta}>
                      {cratesForGroup.length}{" "}
                      {cratesForGroup.length === 1 ? "crate" : "crates"} ·{" "}
                      {groupCxCount} customers
                    </Text>
                  </View>
                </TouchableOpacity>
                {cratesForGroup.map(({ crate, cxs }) => {
                  const id = String(crate?.crate_id || crate?.title);
                  // Key by locality + crate: a shared crate appears under
                  // multiple localities, so its expand state must not
                  // collide across groups.
                  const rowKey = `${String(g.route?.route_id)}|${id}`;
                  const isOpen = openCrates.has(rowKey);
                  const state = crateState(cxs);
                  // Other DBs that have at least one customer in this
                  // crate-within-this-locality (not on this DB). Used for
                  // the warning chip — checking such a customer MOVES them.
                  const myDbId = String(db?.cp_db_id || "");
                  const otherDbs = (() => {
                    const seen = new Map();
                    cxs.forEach((tp) => {
                      const dId = String(tp?.cp_db_id || "");
                      if (
                        !dId ||
                        dId === myDbId ||
                        dId === "MISROUTE" ||
                        dId === "UNASSIGN"
                      ) {
                        return;
                      }
                      if (tp?.status && tp.status !== "ASSIGNED") return;
                      if (!seen.has(dId)) {
                        seen.set(dId, tp?.db_name || `DB ${dId}`);
                      }
                    });
                    return Array.from(seen.values());
                  })();
                  const otherDbsLabel = otherDbs.length
                    ? otherDbs.length <= 2
                      ? `Also on: ${otherDbs.join(", ")}`
                      : `Also on: ${otherDbs.slice(0, 2).join(", ")} +${otherDbs.length - 2} more`
                    : null;
                  return (
                    <View key={rowKey} style={styles.crateBlock}>
                      <View
                        style={[
                          styles.crateRow,
                          otherDbs.length > 0 && styles.crateRowOnOtherDb,
                        ]}
                      >
                        <TouchableOpacity
                          activeOpacity={0.85}
                          onPress={() => toggleCrate(cxs)}
                          disabled={cxs.length === 0}
                          style={styles.crateRowLeft}
                        >
                          <View
                            style={[
                              styles.checkbox,
                              state === "all" && styles.checkboxSelected,
                              state === "some" && styles.checkboxPartial,
                            ]}
                          >
                            {state === "all" ? (
                              <Text variant="caption_2" style={styles.tick}>
                                ✓
                              </Text>
                            ) : state === "some" ? (
                              <Text variant="caption_2" style={styles.tick}>
                                ─
                              </Text>
                            ) : null}
                          </View>
                          <View style={styles.crateBody}>
                            <Text
                              variant="caption_2"
                              style={styles.crateTitle}
                              numberOfLines={1}
                            >
                              {crate?.title || id}
                            </Text>
                            <Text variant="caption_2" style={styles.crateMeta}>
                              {cxs.length}{" "}
                              {cxs.length === 1 ? "customer" : "customers"}
                              {crate?.crate_size
                                ? ` · ${crate.crate_size}`
                                : ""}
                            </Text>
                            {otherDbsLabel ? (
                              <Text
                                variant="caption_2"
                                style={styles.crateOnOther}
                                numberOfLines={1}
                              >
                                {otherDbsLabel}
                              </Text>
                            ) : null}
                          </View>
                        </TouchableOpacity>
                        <View style={styles.crateRowRight}>
                          {crate?.is_shipment ? (
                            <StatusBadge label="Shipment" state="rescheduled" />
                          ) : null}
                          {crate?.is_rescheduled ? (
                            <StatusBadge label="Resched" state="rescheduled" />
                          ) : null}
                          <TouchableOpacity
                            activeOpacity={0.7}
                            onPress={() => toggleOpen(rowKey)}
                            hitSlop={{
                              top: 8,
                              bottom: 8,
                              left: 8,
                              right: 8,
                            }}
                            style={styles.chevWrap}
                          >
                            <View
                              style={[
                                styles.chev,
                                isOpen ? styles.chevUp : styles.chevDown,
                              ]}
                            >
                              <ChevronRight
                                width={10}
                                height={10}
                                pathFill={tokens.textSecondary}
                              />
                            </View>
                          </TouchableOpacity>
                        </View>
                      </View>
                      {isOpen ? (
                        <View style={styles.cxList}>
                          {cxs.map((tp) => {
                            const cxKey = String(tp?.user_id);
                            const isSel = selectedCx.has(cxKey);
                            const isOnThisDb =
                              String(tp?.cp_db_id || "") === myDbId &&
                              (!tp?.status || tp.status === "ASSIGNED");
                            const onAnotherDb = Boolean(
                              tp?.cp_db_id &&
                                String(tp.cp_db_id) !== myDbId &&
                                tp.cp_db_id !== "MISROUTE" &&
                                tp.cp_db_id !== "UNASSIGN",
                            );
                            return (
                              <TouchableOpacity
                                key={cxKey}
                                activeOpacity={0.85}
                                onPress={() => toggleCx(tp?.user_id)}
                                style={[
                                  styles.cxRow,
                                  isSel && styles.cxRowSelected,
                                ]}
                              >
                                <View
                                  style={[
                                    styles.checkboxSm,
                                    isSel && styles.checkboxSelected,
                                  ]}
                                >
                                  {isSel ? (
                                    <Text
                                      variant="caption_2"
                                      style={styles.tickSm}
                                    >
                                      ✓
                                    </Text>
                                  ) : null}
                                </View>
                                <View style={styles.cxBody}>
                                  <Text
                                    variant="caption_2"
                                    style={styles.cxName}
                                    numberOfLines={1}
                                  >
                                    {tp?.user_name || `Cx ${tp?.user_id}`}
                                  </Text>
                                  {tp?.locality || isOnThisDb || onAnotherDb ? (
                                    <Text
                                      variant="caption_2"
                                      style={styles.cxMeta}
                                      numberOfLines={1}
                                    >
                                      {tp?.locality || ""}
                                      {isOnThisDb
                                        ? `${tp?.locality ? " · " : ""}on this DB`
                                        : onAnotherDb
                                          ? `${tp?.locality ? " · " : ""}reassign from ${tp?.db_name || tp.cp_db_id}`
                                          : ""}
                                    </Text>
                                  ) : null}
                                </View>
                              </TouchableOpacity>
                            );
                          })}
                        </View>
                      ) : null}
                    </View>
                  );
                })}
              </View>
            );
          })}
        </ScrollView>
      )}
    </BottomSheet>
  );
};

export default AddBundleSheet;

const styles = StyleSheet.create({
  empty: {
    color: tokens.textTertiary,
    fontSize: 13,
    textAlign: "center",
    paddingVertical: tokens.spacing.large,
  },
  scroll: { maxHeight: 460 },
  group: { marginBottom: tokens.spacing.medium },
  groupHeader: {
    flexDirection: "row",
    alignItems: "center",
    paddingBottom: tokens.spacing.small,
    marginBottom: tokens.spacing.small,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: tokens.borderLight,
  },
  groupHeaderText: { flex: 1 },
  groupLabel: {
    color: tokens.textTertiary,
    fontSize: 10,
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  groupTitle: { color: tokens.textPrimary, fontSize: 14, fontWeight: "700" },
  groupMeta: {
    color: tokens.textSecondary,
    fontSize: 11,
    marginTop: 2,
  },
  crateBlock: { marginBottom: 6 },
  crateRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: tokens.spacing.medium,
    paddingVertical: tokens.spacing.small,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: tokens.borderLight,
    backgroundColor: tokens.surface,
  },
  crateRowLeft: { flex: 1, flexDirection: "row", alignItems: "center" },
  crateRowRight: { flexDirection: "row", alignItems: "center" },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 4,
    borderWidth: 1.5,
    borderColor: tokens.borderMedium,
    marginRight: tokens.spacing.medium,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: tokens.surface,
  },
  checkboxSelected: {
    backgroundColor: tokens.primary.bg,
    borderColor: tokens.primary.bg,
  },
  checkboxPartial: {
    backgroundColor: tokens.warning,
    borderColor: tokens.warning,
  },
  checkboxSm: {
    width: 18,
    height: 18,
    borderRadius: 4,
    borderWidth: 1.5,
    borderColor: tokens.borderMedium,
    marginRight: tokens.spacing.small,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: tokens.surface,
  },
  tick: { color: tokens.primary.fg, fontSize: 14, fontWeight: "700" },
  tickSm: { color: tokens.primary.fg, fontSize: 12, fontWeight: "700" },
  crateBody: { flex: 1 },
  crateTitle: { color: tokens.textPrimary, fontSize: 13, fontWeight: "600" },
  crateMeta: { color: tokens.textSecondary, fontSize: 12, marginTop: 2 },
  crateRowOnOtherDb: {
    borderColor: tokens.warning,
    backgroundColor: tokens.bg,
  },
  crateOnOther: {
    color: tokens.warning,
    fontSize: 11,
    marginTop: 2,
    fontWeight: "600",
  },
  chevWrap: { marginLeft: tokens.spacing.small },
  chev: {
    width: 12,
    height: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  chevDown: { transform: [{ rotate: "90deg" }] },
  chevUp: { transform: [{ rotate: "-90deg" }] },
  cxList: {
    marginLeft: tokens.spacing.large,
    marginTop: tokens.spacing.tiny,
  },
  cxRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: tokens.spacing.small,
    paddingVertical: 6,
    borderRadius: tokens.radius.sm,
  },
  cxRowSelected: { backgroundColor: tokens.pill.packed.bg },
  cxBody: { flex: 1 },
  cxName: { color: tokens.textPrimary, fontSize: 12, fontWeight: "600" },
  cxMeta: { color: tokens.textSecondary, fontSize: 11, marginTop: 2 },
});
